import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lightbulb } from "lucide-react";
import { useLanguage } from "../i18n";

const SCENES = ["residenziale", "ufficio", "retail", "ristorante", "esterno"];
const AUTOPLAY = 6000;

export const RelightTeaser = () => {
  const { t } = useLanguage();
  const r = t.relightTeaser;
  const [scene, setScene] = useState(0);
  const [after, setAfter] = useState(true);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setTimeout(() => { setScene((s) => (s + 1) % SCENES.length); setAfter(true); }, AUTOPLAY);
    return () => clearTimeout(timer);
  }, [scene, paused]);

  const key = SCENES[scene];
  const src = `/relight/${key}-${after ? "after" : "before"}.jpg`;
  const pick = (i) => { setScene(i); setAfter(true); setPaused(true); };

  return (
    <section data-testid="relight-teaser" className="day-light-bg relight-sec py-20 lg:py-28 bg-espresso text-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <div className="relative rounded-sm overflow-hidden border border-cream/10 aspect-[3/2] bg-black">
            <AnimatePresence mode="sync">
              <motion.img key={src} data-testid={`relight-teaser-image-${key}-${after ? "after" : "before"}`} src={src} alt={`${r.scenes[key]} — ${after ? r.after : r.before}`}
                className="absolute inset-0 w-full h-full object-cover" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7, ease: "easeInOut" }} />
            </AnimatePresence>
            <div className="absolute top-4 left-4 bg-espresso/70 backdrop-blur-md rounded-sm px-3 h-8 flex items-center text-[11px] uppercase tracking-[0.2em] font-semibold text-amberl" data-testid="relight-teaser-scene-label">{r.scenes[key]}</div>
            <div className="absolute bottom-4 left-4 flex gap-1 bg-espresso/80 backdrop-blur-md rounded-sm p-1 text-[11px] font-semibold uppercase tracking-[0.15em]">
              <button type="button" data-testid="relight-teaser-before" onClick={() => { setAfter(false); setPaused(true); }} className={`px-3 h-8 rounded-sm transition-colors ${!after ? "bg-amberl text-espresso" : "text-cream/70 hover:text-cream"}`}>{r.before}</button>
              <button type="button" data-testid="relight-teaser-after" onClick={() => { setAfter(true); setPaused(true); }} className={`px-3 h-8 rounded-sm transition-colors ${after ? "bg-amberl text-espresso" : "text-cream/70 hover:text-cream"}`}>{r.after}</button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="relight-teaser-scenes">
            {SCENES.map((k, i) => (
              <button key={k} type="button" data-testid={`relight-teaser-scene-${k}`} onClick={() => pick(i)}
                className={`relight-scene px-4 h-9 rounded-sm text-[11px] font-semibold uppercase tracking-[0.15em] border transition-colors ${i === scene ? "bg-amberl text-espresso border-amberl" : "border-cream/20 text-cream/70 hover:border-cream/60 hover:text-cream"}`}>
                {r.scenes[k]}
              </button>
            ))}
          </div>
        </motion.div>
        <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }}>
          <p className="relight-overline flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-amberl mb-6"><Lightbulb className="w-4 h-4" strokeWidth={1.5} />{r.overline}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight">{r.title}</h2>
          <p className="relight-text mt-6 text-base text-cream/70 leading-relaxed">{r.desc}</p>
          {r.note ? <p className="relight-text mt-4 text-xs text-cream/50">{r.note}</p> : null}
        </motion.div>
      </div>
    </section>
  );
};
